
interface TextToSpeechOptions {
  text: string;
  lang?: string;
  rate?: number;
  pitch?: number;
  volume?: number;
}

/**
 * Service for browser-based text-to-speech functionality
 */
export const textToSpeechService = {
  /**
   * Check if speech synthesis is supported in the current browser
   */
  isSupported(): boolean {
    return 'speechSynthesis' in window;
  },

  /**
   * Get available voices
   */
  getVoices(): SpeechSynthesisVoice[] {
    if (!this.isSupported()) return [];
    
    // In some browsers, getVoices() may return an empty array if called too early
    const voices = window.speechSynthesis.getVoices();
    if (voices.length === 0) {
      console.log("No voices available yet, trying to load voices");
    }
    return voices;
  },

  /**
   * Get voices for a specific language
   * @param langCode - Language code (e.g., 'ur', 'en-US')
   */
  getVoicesForLanguage(langCode: string): SpeechSynthesisVoice[] {
    if (!this.isSupported()) return [];
    const voices = this.getVoices();
    return voices.filter(voice => voice.lang.startsWith(langCode));
  },

  /**
   * Speak the provided text using browser's Speech Synthesis API
   */
  speak({ text, lang = 'ur', rate = 0.9, pitch = 1, volume = 1 }: TextToSpeechOptions): void {
    if (!this.isSupported() || !text) {
      console.log("Speech synthesis not supported or no text provided");
      return;
    }
    
    // Cancel any ongoing speech
    this.stop();

    // Create utterance
    const utterance = new SpeechSynthesisUtterance(text);
    
    // Set utterance properties
    utterance.lang = lang;
    utterance.rate = rate; // Slightly slower for better clarity
    utterance.pitch = pitch;
    utterance.volume = volume;
    
    // Get all available voices
    const voices = this.getVoices();
    console.log("Available voices:", voices.map(v => `${v.name} (${v.lang})`));
    
    // Try to find an Urdu voice
    let urduVoices = voices.filter(v => v.lang.includes('ur'));
    
    // If no specific Urdu voices found, try Hindi (close language) or Arabic voices
    if (urduVoices.length === 0) {
      console.log("No Urdu voices found, trying Hindi or Arabic voices");
      urduVoices = voices.filter(v => v.lang.includes('hi') || v.lang.includes('ar'));
    }
    
    // If we found a suitable voice, use it
    if (urduVoices.length > 0) {
      console.log("Using voice:", urduVoices[0].name, urduVoices[0].lang);
      utterance.voice = urduVoices[0];
    } else {
      // If no suitable voice found, use default but still specify Urdu language
      console.log("No suitable voice found, using default with Urdu language setting");
    }

    // Add event handlers for debugging
    utterance.onstart = () => console.log("Speech started");
    utterance.onend = () => console.log("Speech ended");
    utterance.onerror = (event) => console.error("Speech error:", event);

    // Speak the text
    console.log("Speaking text:", text.substring(0, 50) + "...");
    window.speechSynthesis.speak(utterance);
  },

  /**
   * Stop any ongoing speech
   */
  stop(): void {
    if (!this.isSupported()) return;
    window.speechSynthesis.cancel();
  },

  /**
   * Pause ongoing speech
   */
  pause(): void {
    if (!this.isSupported()) return;
    window.speechSynthesis.pause();
  },

  /**
   * Resume paused speech
   */
  resume(): void {
    if (!this.isSupported()) return;
    window.speechSynthesis.resume();
  },
};
